import { CustomerBillingModel, CustomerModel } from 'api-hooks/customer';
import { RefundModel } from 'api-hooks/refund/refund';
import { SaleDetailModel } from 'api-hooks/sale/model';
import { Option } from 'baseui/select';
import { SaleTransactionTypeEnum } from 'common/constants/enum';
import { capitalize, transformLabel } from 'common/utils/string';
import { useFormState } from 'components/elements/form';
import Text from 'components/elements/text';
import { debounce } from 'lodash';
import CustomerSelectInput from 'legacy-modules/master/customer/components/select-input';
import RefundSalesSelectInput from 'legacy-modules/refund/components/refund-sales-select-input';
import CustomerBillingSelectInput from 'legacy-modules/sales/component/customer-billing-select-input';
import { guestNameTransformer } from 'legacy-modules/sales/ticket-sale/hooks/use-get-default-values';
import {
  HalfContainer,
  SectionContainer,
  SeparatorVertical,
} from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFormContext } from 'react-hook-form';

import SaleRefundField from './sale-refund-field';
import {
  ActivitySaleAddOnsType,
  ActivitySaleGuestDetailType,
  ActivitySaleType,
  CruiseSaleAddOnsType,
  CruiseSaleDetailsType,
  CruiseSaleGuestType,
  CruiseSaleType,
  DocumentSaleAddOnsType,
  DocumentSaleGuestType,
  DocumentSaleType,
  FlightSaleAddOnsType,
  FlightSaleGuestType,
  FlightSaleType,
  HotelSaleAddOnsType,
  HotelSaleGuestType,
  HotelSaleHotelType,
  HotelSaleType,
  ProductSaleType,
  TourSaleAddOnsType,
  TourSaleGuestType,
  TourSaleType,
} from '../form-type';

function transformer(item: CustomerBillingModel): Option {
  return {
    id: item.id,
    label: transformLabel([item.name, item.address, item.phone]),
  };
}

interface Props {
  refund?: RefundModel;
}

export default function InformationCustomerField(props: Props) {
  const { refund } = props;
  const { t } = useTranslation();
  const { setValue, clearErrors, getValues, reset, resetField } =
    useFormContext();
  const { editable } = useFormState();
  // const [billingOptions, setBillingOptions] = React.useState<Option[]>([]);
  const options = React.useMemo(() => {
    return { shouldValidate: true, shouldTouch: true };
  }, []);

  const invoiceId = getValues('releaseSale.id');
  React.useEffect(() => {
    if (!invoiceId || invoiceId === '') {
      reset();
    }
  }, [invoiceId, reset]);

  const customerId = getValues('releaseSale.customerId');
  React.useEffect(() => {
    if (!customerId || customerId === '') {
      setValue('customerBillingOptions', []);
      resetField('customerBillingId');
    }
  }, [customerId, resetField, setValue]);

  const onChangeRefundSales = React.useCallback(
    (params: SaleDetailModel) => {
      setValue('transactionSaleType', params.transactionType);

      const flightSales: FlightSaleType[] = (params.flightSales || []).map(
        (flight) => {
          //Add Ons
          const flightSaleAddOns: FlightSaleAddOnsType[] =
            flight.flightSaleAddOns.map((addons) => {
              const releaseSaleAddOn = refund
                ? refund.releaseSale.releaseSaleAddOns.find(
                    (curr) => curr.addonable.id === addons.id,
                  )
                : undefined;
              return {
                isSelected: !!releaseSaleAddOn,
                description: addons.description,
                id: addons.id,
                route: addons.route,
                price: addons.price,
                guestName: [addons.guest.title, addons.guest.name].join(' '),
                refundPrice: releaseSaleAddOn?.refundPrice ?? 0,
              };
            });
          //Guests
          const flightSaleGuests: FlightSaleGuestType[] =
            flight.flightSaleGuests
              .map((fsg) => {
                const releaseSaleItem = refund
                  ? refund.releaseSale.releaseSaleItems.find(
                      (curr) => curr.refundable.id === fsg.id,
                    )
                  : undefined;
                return {
                  isSelected: !!releaseSaleItem,
                  ticket: fsg.ticket,
                  checkInStatus: fsg.checkInStatus,
                  preferences: [],
                  airlineName: fsg.airline.name,
                  preferenceName: fsg.preference?.name,
                  date: fsg.date,
                  detail: fsg.detail,
                  route: fsg.route,
                  time: fsg.time,
                  id: fsg.id,
                  guestId: fsg.guest.id,
                  guestName: guestNameTransformer(fsg.guest),
                  price: fsg.price,
                  tempPrice: fsg.price,
                  refundPrice: releaseSaleItem?.refundPrice ?? 0,
                };
              })
              .flat();

          const isSelectedItems =
            flightSaleAddOns.some((fsao) => !!fsao.isSelected) ||
            flightSaleGuests.some((fsg) => !!fsg.isSelected);

          return {
            isSelected: isSelectedItems,
            id: flight.id,
            bookingCode: flight.bookingCode,
            flightSaleAddOns,
            flightSaleGuests,
          };
        },
      );
      setValue('flightSales', flightSales);

      const hotelSales: HotelSaleType[] = (params.hotelSales || []).map(
        (hs) => {
          //guest
          const hotelSaleGuests: HotelSaleGuestType[] = hs.hotelSaleGuests.map(
            (hsg) => ({
              guestId: hsg.guest.id,
              guestName: guestNameTransformer(hsg.guest),
              guestPreferences: (hsg.guest.preferenceStats || []).map(
                (ggps) => ({
                  id: ggps.id,
                  label: `${ggps.name} ${
                    ggps.count > 1 ? `(X${ggps.count})` : ''
                  }`,
                }),
              ),
              preferences: hsg.preferences.map((item) => ({
                id: item.id,
                label: item.name,
              })),
              id: hsg.id,
              isLead: hsg.isLead,
            }),
          );

          //hotel
          const hotelSaleHotels: HotelSaleHotelType[] = hs.hotelSaleHotels.map(
            (hsh) => {
              const releaseSaleItem = refund
                ? refund.releaseSale.releaseSaleItems.find(
                    (curr) => curr.refundable.id === hsh.id,
                  )
                : undefined;
              return {
                checkInStatus: hsh.checkInStatus,
                checkIn: hsh.checkIn,
                checkOut: hsh.checkOut,
                hotelConfirmationCode: hsh.hotelConfirmationCode || '',
                detail: hsh.detail,
                pax: hsh.pax || 0,
                hotelRoomId: hsh.hotelRoom.id,
                id: hsh.id,
                night: hsh.night,
                price: hsh.price,
                room: hsh.room,
                refundPrice: releaseSaleItem?.refundPrice ?? 0,
                isSelected: !!releaseSaleItem,
              };
            },
          );

          //addons
          const hotelSaleAddOns: HotelSaleAddOnsType[] = hs.hotelSaleAddOns.map(
            (hsao) => {
              const releaseSaleAddOn = refund
                ? refund.releaseSale.releaseSaleAddOns.find(
                    (curr) => curr.addonable.id === hsao.id,
                  )
                : undefined;
              return {
                description: hsao.description,
                guestId: hsao.guest.id,
                guestName: guestNameTransformer(hsao.guest),
                id: hsao.id,
                isSelected: !!releaseSaleAddOn,
                price: hsao.price,
                priceUnit: hsao.priceUnit || 0,
                unit: hsao.unit || 0,
                refundPrice: releaseSaleAddOn?.refundPrice ?? 0,
              };
            },
          );

          const isSelectedItems =
            hotelSaleHotels.some((hsh) => !!hsh.isSelected) ||
            hotelSaleAddOns.some((hsao) => !!hsao.isSelected);

          return {
            isSelected: !!isSelectedItems,
            bookingCode: hs.bookingCode,
            cityTax: hs.cityTax,
            hotelId: hs.hotel.id,
            id: hs.id,
            isCityTax: hs.isCityTax,
            isByPax: hs.isByPax,
            // remark: hs.remark,
            hotelSaleGuests,
            hotelSaleHotels,
            hotelSaleAddOns,
          };
        },
      );

      setValue('hotelSales', hotelSales);

      const cruiseSales: CruiseSaleType[] = (params.cruiseSales || []).map(
        (cs) => {
          //guest
          const cruiseSaleGuests: CruiseSaleGuestType[] =
            cs.cruiseSaleGuests.map((csg) => {
              return {
                guestId: csg.guest.id,
                id: csg.id,
                preferences: [],
                guestName: guestNameTransformer(csg.guest),
              };
            });

          // details
          const cruiseSaleDetails: CruiseSaleDetailsType[] =
            cs.cruiseSaleDetails.map((csd) => {
              const releaseSaleItem = refund
                ? refund.releaseSale.releaseSaleItems.find(
                    (curr) => curr.refundable.id === csd.id,
                  )
                : undefined;
              const guest = cs.cruiseSaleGuests.find(
                (csg) => csg.id === csd.cruiseSaleGuestId,
              )?.guest;

              return {
                basicFare: csd.basicFare,
                cruiseRoomId: csd.cruiseRoom.id,
                cruiseRoomNumber: csd.cruiseRoomNumber,
                deck: csd.deck,
                description: csd.description,
                guestId: guest?.id || '',
                id: csd.id,
                isSelected: !!releaseSaleItem,
                portCharge: csd.portCharge,
                tax: csd.tax,
                tipping: csd.tipping,
                guestName: !cs.isGroupedPrice
                  ? guestNameTransformer(guest!)
                  : cs.cruiseSaleGuests
                      .map((g) => guestNameTransformer(g.guest))
                      .join(', '),

                refundPrice: releaseSaleItem?.refundPrice ?? 0,
              };
            });

          //addons
          const cruiseSaleAddOns: CruiseSaleAddOnsType[] =
            cs.cruiseSaleAddOns.map((csao) => {
              const releaseSaleAddOn = refund
                ? refund.releaseSale.releaseSaleAddOns.find(
                    (curr) => curr.addonable.id === csao.id,
                  )
                : undefined;
              return {
                description: csao.description,
                guestId: csao.guest.id,
                guestName: guestNameTransformer(csao.guest),
                isSelected: !!releaseSaleAddOn,
                price: csao.price,
                id: csao.id,
                refundPrice: releaseSaleAddOn?.refundPrice ?? 0,
              };
            });

          const isSelectedItems =
            cruiseSaleDetails.some((csd) => !!csd.isSelected) ||
            cruiseSaleAddOns.some((csao) => !!csao.isSelected);

          const informationCruiseSale = {
            bookingCode: cs.bookingCode,
            cruiseId: cs.cruise.id,
            cruiseShipId: cs.cruiseShip?.id,
            endedAt: cs.endedAt,
            startedAt: cs.startedAt,
            id: cs.id,
            isSelected: !!isSelectedItems,
            isGroupedPrice: cs.isGroupedPrice,
            reminder: cs.reminder,
            reminderDate: cs.reminderDate,
            reminderdescription: cs.reminderDescription,
            route: cs.route,
            cruiseName: cs.cruise.name,
            cruiseShipName: cs.cruiseShip?.name,
            cruiseRooms: cs.cruise.cruiseRooms.map((room) => ({
              id: room.id,
              label: `${room.type} (${room.size})`,
            })),
          };

          return {
            ...informationCruiseSale,
            cruiseSaleAddOns,
            cruiseSaleGuests,
            cruiseSaleDetails,
          };
        },
      );

      setValue('cruiseSales', cruiseSales);

      const activitySales: ActivitySaleType[] = (
        params.activitySales || []
      ).map((as) => {
        //guest
        const activitySaleGuests: ActivitySaleGuestDetailType[] =
          as.activitySaleGuests
            .map((asg) => {
              return asg.activitySaleGuestDetails.map((asgd) => {
                const releaseSaleItem = refund
                  ? refund.releaseSale.releaseSaleItems.find((curr) => {
                      return curr.refundable.id === asgd.id;
                    })
                  : undefined;
                return {
                  guestId: asg.guest.id,
                  guestName: guestNameTransformer(asg.guest),
                  activityId: asgd.activity.id,
                  activityName: asgd.activity.name,
                  date: asgd.date,
                  description: asgd.description,
                  id: asgd.id,
                  isSelected: !!releaseSaleItem,
                  price: asgd.totalPrice,
                  type: capitalize(asgd.activity.type),
                  refundPrice: releaseSaleItem?.refundPrice ?? 0,
                };
              });
            })
            .flat();

        //addons
        const activitySaleAddOns: ActivitySaleAddOnsType[] =
          as.activitySaleAddOns.map((asao) => {
            const releaseSaleAddOn = refund
              ? refund.releaseSale.releaseSaleAddOns.find(
                  (curr) => curr.addonable.id === asao.id,
                )
              : undefined;
            return {
              description: asao.description,
              guestId: asao.guest.id,
              guestName: guestNameTransformer(asao.guest),
              id: asao.id,
              isSelected: !!releaseSaleAddOn,
              price: asao.price,
              refundPrice: releaseSaleAddOn?.refundPrice ?? 0,
            };
          });

        return {
          id: as.id,
          isGroupedPrice: as.isGroupedPrice,
          activitySaleAddOns,
          activitySaleGuests,
        };
      });

      setValue('activitySales', activitySales);

      const documentSales: DocumentSaleType[] = (
        params.documentSales || []
      ).map((ds) => {
        const documentSaleAddOns: DocumentSaleAddOnsType[] =
          ds.documentSaleAddOns.map((dsao) => {
            const releaseSaleAddOn = refund
              ? refund.releaseSale.releaseSaleAddOns.find(
                  (curr) => curr.addonable.id === dsao.id,
                )
              : undefined;
            return {
              description: dsao.description,
              guestId: dsao.guest.id,
              guestName: guestNameTransformer(dsao.guest),
              id: dsao.id,
              price: dsao.price,
              isSelected: !!releaseSaleAddOn,
              refundPrice: releaseSaleAddOn?.refundPrice ?? 0,
            };
          });

        const documentSaleGuests: DocumentSaleGuestType[] =
          ds.documentSaleGuests
            .map((dsg) => {
              const releaseSaleItem = refund
                ? refund.releaseSale.releaseSaleItems.find(
                    (curr) =>
                      curr.refundable.id === ((dsg.details as any)?.id as any),
                  )
                : undefined;
              if (Array.isArray(dsg.details)) {
                return dsg.details.map((detail) => {
                  const releaseSaleItem = refund
                    ? refund.releaseSale.releaseSaleItems.find(
                        (curr) => curr.refundable.id === detail.id,
                      )
                    : undefined;
                  return {
                    guestId: dsg.guest.id,
                    guestName: guestNameTransformer(dsg.guest),

                    id: detail.id,
                    preferenceName: detail.visaDocument?.name || '',
                    appointmentAt: detail.appointmentAt || new Date(),
                    biometric: detail.biometric,

                    type: detail.type,
                    package: detail.package,
                    coverArea: detail.coverArea,
                    company: detail.company,
                    days: detail.days,
                    endAt: detail.endAt,
                    startAt: detail.startAt,

                    description: detail.description,
                    price: detail.price,
                    isSelected: !!releaseSaleItem,
                    refundPrice: releaseSaleItem?.refundPrice ?? 0,
                  };
                });
              }
              return {
                guestId: dsg.guest.id,
                guestName: guestNameTransformer(dsg.guest),
                id: dsg.details.id,
                description: dsg.details.description,
                price: dsg.details.price,
                type: dsg.details.type,
                isSelected: !!releaseSaleItem,
                refundPrice: releaseSaleItem?.refundPrice ?? 0,
              };
            })
            .flat();

        return {
          id: ds.id,
          documentType: ds.documentType,
          documentSaleAddOns,
          documentSaleGuests,
        };
      });

      setValue('documentSales', documentSales);

      const tourSales: TourSaleType[] = (params.tourSales || []).map((ts) => {
        const tourSaleAddOns: TourSaleAddOnsType[] = ts.tourSaleAddOns.map(
          (tsao) => {
            const releaseSaleAddOn = refund
              ? refund.releaseSale.releaseSaleAddOns.find(
                  (curr) => curr.addonable.id === tsao.id,
                )
              : undefined;
            return {
              description: tsao.description,
              guestId: tsao.guest.id,
              guestName: guestNameTransformer(tsao.guest),
              id: tsao.id,
              price: tsao.price,
              isSelected: !!releaseSaleAddOn,
              refundPrice: releaseSaleAddOn?.refundPrice ?? 0,
            };
          },
        );
        const tourSaleGuests: TourSaleGuestType[] = ts.tourSaleGuests.map(
          (tsg) => {
            const releaseSaleItem = refund
              ? refund.releaseSale.releaseSaleItems.find(
                  (curr) => curr.refundable.id === tsg.id,
                )
              : undefined;
            return {
              bed: tsg.bedType,
              group: tsg.groupNumber,
              guestId: tsg.guest.id,
              guestName: guestNameTransformer(tsg.guest),
              id: tsg.id,
              note: tsg.note,
              price: tsg.price,
              schedule: tsg.tourFlight?.name || '',
              type: tsg.tourPrice?.type || '',
              isSelected: !!releaseSaleItem,
              refundPrice: releaseSaleItem?.refundPrice ?? 0,
            };
          },
        );
        return {
          id: ts.id,
          tourSaleAddOns,
          tourSaleGuests,
        };
      });

      setValue('tourSales', tourSales);

      const productSales: ProductSaleType[] = (params.productSales || []).map(
        (ps) => {
          const releaseSaleItem = refund
            ? refund.releaseSale.releaseSaleItems.find(
                (curr) => curr.refundable.id === ps.id,
              )
            : undefined;

          return {
            id: ps.id,
            description: ps.description,
            price: ps.price,
            productVariantId: ps.productVariant.id,
            productVariantName: ps.completeName,
            qty: ps.qty,
            refundPrice: releaseSaleItem?.refundPrice ?? 0,
            isSelected: !!releaseSaleItem,
          };
        },
      );
      setValue('productSales', productSales);

      if (editable) {
        const _releaseSale = getValues('releaseSale');
        const releaseSale = {
          ..._releaseSale,
          id: params.id,
          refId: params.refId,
          customerId: params.customer.id,
          customerBillingId: params.customerBillingAddress?.id ?? null,
          code: params.code,
          description: params.description,
          transactionAt: new Date(params.transactionAt),
          total: params.total,
          adminId: params.admin.id,
          salesmanId: params.salesman?.id,
          operatorId: params.operator?.id,
        };

        setValue(
          'releaseSale',
          {
            ...releaseSale,
            previousSale: releaseSale,
            // amount: 0, // YOSA TODO: CHECK THIS VALUE
          },
          options,
        );

        switch (params.transactionType) {
          case SaleTransactionTypeEnum.Tour:
          case SaleTransactionTypeEnum.Product:
            setValue('isHideAgent', true);
            break;
          default:
            setValue('isHideAgent', true);
            debounce(() => {
              setValue('isHideAgent', false);
            }, 300)();
            break;
        }
        setValue('flightPurchases', []);
        setValue('hotelPurchases', []);
        setValue('cruisePurchases', []);
        setValue('activityPurchases', []);
        setValue('documentPurchases', []);

        setValue('releasePurchase', null);
        clearErrors();
      }
    },
    [setValue, editable, refund, getValues, options, clearErrors],
  );

  const onChangeCustomer = React.useCallback(
    ({ billings, id }: CustomerModel) => {
      if (editable) {
        if (refund) {
          setValue('customerId', id);
        } else {
          setValue('releaseSale.customerId', id);
        }
      }
      const _options = billings?.map((billing) => transformer(billing)) || [];
      setValue('customerBillingOptions', _options);

      if (editable) {
        setValue(
          'releaseSale.customerBillingId',
          _options.length ? _options[0].id : '',
        );
      }
    },
    [editable, refund, setValue],
  );

  return (
    <>
      {!refund && (
        <>
          <Text variant="HeadingSmall">{t('refund:customer_refund')}</Text>
          <SeparatorVertical $margin={36} />
        </>
      )}
      <SectionContainer>
        <HalfContainer>
          <RefundSalesSelectInput
            key={refund?.id}
            name="releaseSale.id"
            label={t('common:invoice_number')}
            placeholder={t('common:invoice_number')}
            required
            onAfterDetailChange={onChangeRefundSales}
            disabled={!!props.refund}
          />
        </HalfContainer>
        <HalfContainer>
          <SaleRefundField />
        </HalfContainer>
      </SectionContainer>
      <SeparatorVertical $margin={16} />
      <SectionContainer>
        <HalfContainer>
          <CustomerSelectInput
            key={refund?.id}
            name="releaseSale.customerId"
            required
            onAfterDetailChange={onChangeCustomer}
          />
        </HalfContainer>
        <HalfContainer>
          <CustomerBillingSelectInput name="releaseSale.customerBillingId" />
        </HalfContainer>
      </SectionContainer>
    </>
  );
}
